import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/privacy-policy')({
  component: RouteComponent,
})

function RouteComponent() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <Link to="/">
          <Button
            variant="ghost"
            className="mb-8 text-frost/70 hover:text-frost"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-frost mb-2">Privacy Policy</h1>
          <p className="text-frost/50 text-sm mb-8">
            Last updated: December 20, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              1. Introduction
            </h2>
            <p className="text-frost/70">
              Snow Spot, LLC ("Snow Spot," "we," "our," or "us") operates the
              website https://snowspot.app and related mobile applications
              (collectively, the "Service").
            </p>
            <p className="text-frost/70">
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our Service. By using Snow
              Spot, you agree to the collection and use of information in
              accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              2. Information We Collect
            </h2>

            <h3 className="text-xl font-medium text-frost mb-3">
              Personal Information
            </h3>
            <p className="text-frost/70 mb-2">
              When you create an account or use our Service, we may collect:
            </p>
            <ul className="text-frost/70 list-disc pl-6 mb-4">
              <li>Name</li>
              <li>Email address</li>
              <li>
                Payment information (processed securely by Stripe or app store
                providers)
              </li>
              <li>Account credentials</li>
              <li>Communication preferences</li>
            </ul>
            <p className="text-frost/70">
              We do not store full payment card numbers on our servers.
            </p>

            <h3 className="text-xl font-medium text-frost mb-3 mt-6">
              Usage Data
            </h3>
            <p className="text-frost/70">
              We automatically collect certain information, including:
            </p>
            <ul className="text-frost/70 list-disc pl-6">
              <li>Device type, operating system, and browser</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-frost/70 mb-2">
              We use the information we collect to:
            </p>
            <ul className="text-frost/70 list-disc pl-6">
              <li>Provide and operate the Service</li>
              <li>Personalize snow data and forecasts</li>
              <li>Process subscriptions and payments</li>
              <li>Communicate updates, alerts, and support messages</li>
              <li>Improve product performance and features</li>
              <li>Monitor usage and prevent abuse or fraud</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              4. Sharing of Information
            </h2>
            <p className="text-frost/70 mb-2">
              We do not sell your personal information.
            </p>
            <p className="text-frost/70 mb-2">We may share information with:</p>
            <ul className="text-frost/70 list-disc pl-6 mb-4">
              <li>Service providers (e.g., Stripe, analytics tools)</li>
              <li>Legal authorities if required by law</li>
              <li>
                Business successors in the event of a merger or acquisition
              </li>
            </ul>
            <p className="text-frost/70">
              All third-party providers are required to protect your
              information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              5. Cookies & Tracking
            </h2>
            <p className="text-frost/70 mb-2">
              Snow Spot may use cookies or similar technologies to:
            </p>
            <ul className="text-frost/70 list-disc pl-6 mb-4">
              <li>Maintain user sessions</li>
              <li>Analyze site usage</li>
              <li>Improve performance and user experience</li>
            </ul>
            <p className="text-frost/70">
              You may disable cookies in your browser settings, though some
              features may not function properly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              6. Data Security
            </h2>
            <p className="text-frost/70">
              We implement reasonable administrative, technical, and physical
              safeguards to protect your information. However, no method of
              transmission over the internet is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              7. Your Rights
            </h2>
            <p className="text-frost/70 mb-2">
              Depending on your location, you may have the right to:
            </p>
            <ul className="text-frost/70 list-disc pl-6 mb-4">
              <li>Access your personal data</li>
              <li>Request correction or deletion</li>
              <li>Opt out of marketing communications</li>
            </ul>
            <p className="text-frost/70">
              Requests can be sent to:{' '}
              <a
                href="mailto:info@snowspot.app"
                className="text-ice hover:underline"
              >
                info@snowspot.app
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              8. Children's Privacy
            </h2>
            <p className="text-frost/70">
              Snow Spot is not intended for users under the age of 13. We do not
              knowingly collect personal data from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              9. Changes to This Policy
            </h2>
            <p className="text-frost/70">
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated effective date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              10. Contact Us
            </h2>
            <p className="text-frost/70">
              If you have questions about this Privacy Policy, contact us at:{' '}
              <a
                href="mailto:info@snowspot.app"
                className="text-ice hover:underline"
              >
                info@snowspot.app
              </a>
            </p>
          </section>
        </article>
      </div>
    </div>
  )
}
